import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { HomeNavButton } from '../../components/NavButtons';
import { useGradingSystem } from '../../contexts/GradingSystemContext';
import { getReportCards } from '../../services/gradeService';
import { resolveGradeLabel } from '../../utils/gradingUtils';

function ArrowCircleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8.63612 5.41078L6.69336 7.21477C6.38802 7.4983 6.38802 7.98157 6.69336 8.2651L8.63612 10.0691M7.70447 0.537475C3.74645 0.537475 0.537843 3.74608 0.537843 7.7041C0.537843 11.6621 3.74645 14.8707 7.70447 14.8707C11.6625 14.8707 14.8711 11.6621 14.8711 7.7041C14.8711 3.74608 11.6625 0.537475 7.70447 0.537475Z" stroke="white" strokeWidth="1.07499" strokeLinecap="round" />
    </svg>
  );
}

function formatReportCardDate(value) {
  if (!value) {
    return 'بدون تاریخ';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'بدون تاریخ';
  }

  return new Intl.DateTimeFormat('fa-IR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function getTeacherName(teacher) {
  return [teacher?.firstName, teacher?.lastName].filter(Boolean).join(' ') || 'معلم';
}

function getClassLabel(reportCard) {
  const classTitle = reportCard.class?.title;
  const gradeTitle = reportCard.class?.grade?.title;

  if (classTitle && gradeTitle) {
    return `${classTitle} · ${gradeTitle}`;
  }

  return classTitle || gradeTitle || 'کلاس';
}

function hasDescription(value) {
  if (value == null) {
    return false;
  }

  if (typeof value !== 'string') {
    return String(value).trim().length > 0;
  }

  return value.trim().length > 0;
}

function GradesPage() {
  const { gradingSystem, disciplineSystem } = useGradingSystem();
  const [reportCards, setReportCards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const totalSubjects = useMemo(
    () => reportCards.reduce((count, reportCard) => count + (reportCard.items?.length || 0), 0),
    [reportCards]
  );

  useEffect(() => {
    let isMounted = true;

    async function loadReportCards() {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const items = await getReportCards();

        if (isMounted) {
          setReportCards(items);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'امکان دریافت کارنامه وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadReportCards();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <p
            style={{
              background: 'linear-gradient(90deg, #A7D2FB 0%, #49A8F4 100%)',
              boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)',
            }}
            className="rounded-[10px] px-6 py-1 text-[15px] text-white"
          >
            کارنامه من
          </p>

          <img className="h-[50px]" src="/images/grade.webp" alt="" />
        </div>

        <HomeNavButton />
      </div>

      <div>
        <p className="text-[11px]">کارنامه تحصیلی</p>
        <p className="text-[11px] font-light">
          کارنامه‌های ثبت‌شده توسط معلم را اینجا ببین و جزئیات هر کارنامه را باز کن.
        </p>
      </div>

      <div
        className="grid grid-cols-2 gap-2 rounded-[8px] p-2"
        style={{ background: 'rgba(217, 217, 217, 0.3)' }}
      >
        <div
          className="rounded-[6px] px-3 py-2 text-white"
          style={{ background: 'rgba(73, 168, 244, 0.7)' }}
        >
          <p className="text-[11px] font-light">تعداد کارنامه</p>
          <p className="mt-1 text-[17px] font-bold">{isLoading ? '...' : reportCards.length}</p>
        </div>

        <div
          className="rounded-[6px] px-3 py-2 text-white"
          style={{ background: '#49A8F4' }}
        >
          <p className="text-[11px] font-light">تعداد دروس</p>
          <p className="mt-1 text-[17px] font-bold">{isLoading ? '...' : totalSubjects}</p>
        </div>
      </div>

      <div
        className="flex flex-col gap-2 rounded-[8px] p-2"
        style={{ background: 'rgba(217, 217, 217, 0.3)' }}
      >
        {isLoading ? (
          <div className="rounded-[8px] bg-white px-4 py-6 text-center text-[12px] text-slate-500">
            در حال دریافت کارنامه...
          </div>
        ) : errorMessage ? (
          <div className="rounded-[8px] bg-white px-4 py-5 text-center">
            <p className="text-[12px] font-bold text-[#E02727]">{errorMessage}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-3 rounded-[6px] bg-[#49A8F4] px-5 py-1 text-[12px] font-bold text-white"
            >
              تلاش دوباره
            </button>
          </div>
        ) : reportCards.length === 0 ? (
          <div className="rounded-[8px] bg-white px-4 py-6 text-center">
            <p className="text-[13px] font-bold text-slate-800">کارنامه‌ای ثبت نشده است</p>
            <p className="mt-2 text-[11px] text-slate-500">
              وقتی معلم کارنامه را ثبت کند، اینجا نمایش داده می‌شود.
            </p>
          </div>
        ) : (
          reportCards.map((reportCard, index) => (
            <Link
              key={reportCard.id}
              to={`/grades/${reportCard.id}`}
              className="flex flex-col gap-2 rounded-[8px] p-3 text-white"
              style={{
                background: 'linear-gradient(90deg, #A7D2FB 0%, #49A8F4 100%)',
                boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)',
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[7px] bg-white text-[12px] font-bold text-[#49A8F4]">
                    {index + 1}
                  </span>

                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-semibold">
                      {reportCard.title || 'بدون عنوان'}
                    </p>
                    <p className="truncate text-[10px] font-light text-white/90">
                      {getClassLabel(reportCard)}
                    </p>
                  </div>
                </div>

                <ArrowCircleIcon />
              </div>

              <div className="flex flex-wrap items-center gap-2 text-[10px]">
                <span className="rounded-[4px] bg-white px-2 py-1 text-[#111827]">
                  {formatReportCardDate(reportCard.date || reportCard.createdAt)}
                </span>
                <span className="rounded-[4px] border border-white/80 px-2 py-1 text-white">
                  معلم: {getTeacherName(reportCard.teacher)}
                </span>
                {reportCard.discipline?.score && (
                  <span className="rounded-[4px] bg-white/20 px-2 py-1 text-white">
                    شایستگی های عمومی:{' '}
                    {resolveGradeLabel(
                      reportCard.discipline.score,
                      reportCard.discipline.scoreLabel,
                      disciplineSystem
                    )}
                  </span>
                )}
              </div>

              {hasDescription(reportCard.discipline?.description) && (
                <p className="line-clamp-2 text-[11px] leading-5 text-white/95">
                  <span className="font-semibold">توضیحات شایستگی های عمومی: </span>
                  {reportCard.discipline.description}
                </p>
              )}

              {(reportCard.items?.length || 0) > 0 && (
                <div className="flex flex-col gap-1.5">
                  {reportCard.items.slice(0, 3).map((item) => (
                    <div
                      key={item.id}
                      className="rounded-[6px] bg-white/15 px-2.5 py-1.5"
                    >
                      <p className="text-[10px] font-semibold text-white">
                        {item.title}:{' '}
                        {resolveGradeLabel(item.score, item.scoreLabel, gradingSystem)}
                      </p>
                      {hasDescription(item.description) && (
                        <p className="mt-0.5 line-clamp-2 text-[10px] leading-5 text-white/90">
                          {item.description}
                        </p>
                      )}
                    </div>
                  ))}
                  {reportCard.items.length > 3 && (
                    <span className="text-[10px] text-white/90">
                      +{reportCard.items.length - 3} درس دیگر
                    </span>
                  )}
                </div>
              )}
            </Link>
          ))
        )}
      </div>
    </div>
  );
}

export default GradesPage;
